import {useEffect, useState} from 'react';
import {useToast} from 'react-native-toast-notifications';
import {useDispatch} from 'react-redux';
import {slices} from 'store';
import {
  combine,
  containsFiles,
  draggable,
  dropTargetForElements,
  dropTargetForExternal,
  getDndData,
  getDroppedFiles,
  getItemDragPreview,
  isDndData,
  moveToFolder,
} from './effects/dnd';

import type {CleanupFn} from '@atlaskit/pragmatic-drag-and-drop/types';
import type {View} from 'react-native';
import type {FilesItem} from 'store/slices/files/types';

interface UseDragDropItemProps {
  ref: React.RefObject<View>;
  target: FilesItem;
  selection: string[];
}

export function useDragDropItem(props: UseDragDropItemProps) {
  const {ref, target, selection} = props;
  const [isDropping, setIsDropping] = useState(false);

  const toast = useToast();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!ref?.current) return;
    const element = ref.current as unknown as HTMLElement;
    const hasDrop = target.type === 'folder';
    const ids = selection.length > 1 ? selection : [target.id];
    return combine(
      ...([
        draggable({
          element,
          getInitialData: () => getDndData(target),
          onGenerateDragPreview: ({nativeSetDragImage}) =>
            getItemDragPreview(nativeSetDragImage, selection),
          onDragStart: () => dispatch(slices.files.actions.drag({ids})),
          onDrop: () => dispatch(slices.files.actions.drag({ids: []})),
        }),
        hasDrop &&
          dropTargetForElements({
            element,
            canDrop: ({source}) =>
              source.element !== element && isDndData(source.data),
            onDragEnter: () => setIsDropping(true),
            onDragLeave: () => setIsDropping(false),
            onDrop: async e => {
              setIsDropping(false);
              if (isDndData(e.source.data)) {
                const {item} = e.source.data;
                const keys = selection.length > 1 ? selection : [item.id];
                toast.show(
                  ...(await moveToFolder(
                    dispatch,
                    keys,
                    target.id,
                    target.name,
                  )),
                );
              }
            },
          }),
        hasDrop &&
          dropTargetForExternal({
            element,
            canDrop: containsFiles,
            getDropEffect: () => 'copy',
            onDragEnter: () => setIsDropping(true),
            onDragLeave: () => setIsDropping(false),
            onDrop: e => {
              setIsDropping(false);
              getDroppedFiles(e.source, files => {
                if (files.length) {
                  let uploadFolder = target.id;
                  dispatch(
                    slices.transfers.actions.upload({
                      id: uploadFolder,
                      files: files
                    }),
                  );
                }
              });
            },
          }),
      ].filter(Boolean) as CleanupFn[]),
    );
  }, [target, selection, ref?.current]);

  return {isDropping};
}
